import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { crmAPI } from '../../lib/api';
import { useCurrency } from '../../lib/CurrencyContext';
import { getCurrencyOptions } from '../../lib/currency';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Skeleton } from '../ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Progress } from '../ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { ScrollArea } from '../ui/scroll-area';
import { CompactBusinessRules } from '../layout/CompactBusinessRules';
import { DataFreshnessBar } from '../layout/DataFreshnessBar';
import { CollectionsTab } from './CollectionsTab';
import { 
  FileText, Search, Eye, DollarSign, Calendar, Clock, AlertTriangle,
  CheckCircle, TrendingUp, Building2, Download, Send, Plus, Filter, 
  RotateCcw, ChevronDown, RefreshCw, Users, Award, Maximize2, Minimize2, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [salespersonData, setSalespersonData] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ accounts: [], years: [], salespersons: [], product_managers: [], solution_categories: [] });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [agingFilter, setAgingFilter] = useState(null);
  const [statusDrill, setStatusDrill] = useState(null);
  const [invoiceNotes, setInvoiceNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('note');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [odooBaseUrl, setOdooBaseUrl] = useState('');
  const [displayMode, setDisplayMode] = useState('compact');
  const { currency: globalCurrency, formatCurrency, updateCurrency } = useCurrency();
  const [selectedCurrency, setSelectedCurrency] = useState(globalCurrency);
  const [searchParams, setSearchParams] = useSearchParams();
  const formatCurrencyCompact = (value, currency) => {
    const cur = currency || selectedCurrency;
    const sym = cur === 'USD' ? '$' : cur === 'AED' ? 'AED ' : cur === 'EUR' ? '€' : `${cur} `;
    const num = Number(value || 0);
    if (Math.abs(num) >= 1000000) return `${sym}${(num / 1000000).toFixed(2)}M`;
    if (Math.abs(num) >= 1000) return `${sym}${(num / 1000).toFixed(1)}K`;
    return formatCurrency(num, cur);
  };

  // Load Odoo base URL for deeplinks
  useEffect(() => {
    crmAPI.getSalesOrdersOdooConfig().then(r => setOdooBaseUrl(r.data?.odoo_base_url || '')).catch(() => {});
  }, []);

  // Contextual filters — pre-populated from URL params if navigating from Accounts page
  const [filters, setFilters] = useState(() => ({
    year: null, quarter: null,
    account: searchParams.get('account') || null,
    salesperson: null, product_manager: null, solution_category: null
  }));
  
  // Active tab pre-set from URL status param (e.g. status=overdue from Accounts page)
  const [activeTab, setActiveTab] = useState(() => searchParams.get('status') || 'all');

  // Show banner when account filter is pre-applied from navigation
  const fromAccountName = searchParams.get('account');

  // Sync with global currency setting
  useEffect(() => {
    setSelectedCurrency(globalCurrency);
  }, [globalCurrency]);

  // Update global currency when changed in this page
  const handleCurrencyChange = (newCurrency) => {
    setSelectedCurrency(newCurrency);
    updateCurrency(newCurrency);
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const displayMoney = (value, currency) => displayMode === 'compact' ? formatCurrencyCompact(value, currency) : formatCurrency(value || 0, currency || selectedCurrency);

  const resetFilters = () => {
    setFilters({ year: null, quarter: null, account: null, salesperson: null, product_manager: null, solution_category: null });
    setActiveTab('all');
  };

  const exportInvoicesRows = async (rows, filename = 'invoices_export.xlsx') => {
    try {
      toast.info('Preparing export...');
      // Use server-side export to get ALL matching invoices (not limited by frontend page size)
      const params = {};
      if (filters.year) params.year = filters.year;
      if (filters.quarter) params.quarter = filters.quarter;
      if (filters.account) params.account = filters.account;
      if (filters.salesperson) params.salesperson = filters.salesperson;
      if (filters.product_manager) params.product_manager = filters.product_manager;
      if (filters.solution_category) params.solution_category = filters.solution_category;
      if (activeTab && !['all', 'salesperson', 'collections'].includes(activeTab)) params.status = activeTab;
      
      const res = await crmAPI.exportInvoicesFiltered(params);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export complete');
    } catch {
      toast.error('Export failed');
    }
  };

  const hasActiveFilters = () => {
    return filters.year || filters.quarter || filters.account || filters.salesperson || filters.product_manager || filters.solution_category;
  };

  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build params based on active tab and filters
      const params = {
        status: ['all', 'salesperson', 'collections'].includes(activeTab) ? null : activeTab,
        year: filters.year,
        quarter: filters.quarter,
        account: filters.account,
        salesperson: filters.salesperson,
        product_manager: filters.product_manager,
        solution_category: filters.solution_category,
        limit: 2000
      };
      
      // Remove null values
      Object.keys(params).forEach(key => {
        if (params[key] === null) delete params[key];
      });
      
      const [invoicesRes, statsRes, salespersonRes] = await Promise.all([
        crmAPI.listReceivables(params),
        crmAPI.getReceivablesStats({ year: filters.year, quarter: filters.quarter, account: filters.account, salesperson: filters.salesperson, product_manager: filters.product_manager, solution_category: filters.solution_category }),
        crmAPI.getReceivablesBySalesperson({ year: filters.year, quarter: filters.quarter, account: filters.account, salesperson: filters.salesperson, product_manager: filters.product_manager, solution_category: filters.solution_category })
      ]);
      
      // Handle new API response format
      const invoiceData = invoicesRes.data?.invoices || invoicesRes.data || [];
      const statsData = statsRes.data?.stats || statsRes.data || {};
      const options = statsRes.data?.filter_options || invoicesRes.data?.filter_options || { accounts: [], years: [] };
      const spData = salespersonRes.data?.data || [];
      
      const mergedStats = {
        ...statsData,
        collection_rate: statsRes.data?.collection_rate ?? invoicesRes.data?.collection_rate,
        aging_breakdown: statsRes.data?.aging_breakdown ?? invoicesRes.data?.aging_breakdown,
      };
      
      setInvoices(invoiceData);
      setStats(mergedStats);
      setFilterOptions(options);
      // Also extract salesperson, PM, category from invoice data
      const salespersons = options.salespersons || [...new Set(invoiceData.map(i => i.salesperson).filter(Boolean))].sort();
      const pms = options.product_managers || [...new Set(invoiceData.map(i => i.product_manager).filter(Boolean))].sort();
      const cats = options.solution_categories || [...new Set(invoiceData.map(i => i.solution_category).filter(Boolean))].sort();
      setFilterOptions(prev => ({ ...prev, ...options, salespersons, product_managers: pms, solution_categories: cats }));
      setSalespersonData(spData);
      
    } catch (error) {
      console.error('Failed to load invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters.year, filters.quarter, filters.account, filters.salesperson, filters.product_manager, filters.solution_category]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: { variant: 'default', icon: CheckCircle, className: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' },
      pending: { variant: 'secondary', icon: Clock, className: 'bg-amber-100 text-amber-700 hover:bg-amber-100' },
      overdue: { variant: 'destructive', icon: AlertTriangle, className: 'bg-red-100 text-red-700 hover:bg-red-100' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {status === 'pending' ? 'Due Soon' : status === 'paid' ? 'Collected' : status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    );
  };

  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch = !searchQuery ||
      inv.account?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.so_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.salesperson?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.product_manager?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.solution_category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.opportunity_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.status?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Status drill-down
    if (statusDrill && inv.status !== statusDrill) return false;
    
    // Aging drill-down filter
    if (agingFilter && inv.status === 'overdue') {
      const days = inv.aging_days || 0;
      if (agingFilter === '0_30' && (days < 0 || days > 30)) return false;
      if (agingFilter === '30_60' && (days <= 30 || days > 60)) return false;
      if (agingFilter === '60_90' && (days <= 60 || days > 90)) return false;
      if (agingFilter === '90_plus' && days <= 90) return false;
    } else if (agingFilter) {
      return false; // Only show overdue when aging filter active
    }
    
    return matchesSearch;
  });

  const collectionRows = filteredInvoices.filter(inv => ['pending', 'overdue'].includes(inv.status));

  const viewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setSheetOpen(true);
    setInvoiceNotes([]);
    setNewNote('');
    setNoteType('note');
    // Load lines, logs, and notes in parallel
    if (invoice?.id) {
      crmAPI.getInvoiceNotes(invoice.id).then(r => setInvoiceNotes(r.data || [])).catch(() => {});
      crmAPI.getInvoiceLines(invoice.id).then(r => setSelectedInvoice(prev => ({ ...prev, _lines: r.data?.lines || [], _lineTotal: r.data }))).catch(() => {});
      crmAPI.getInvoiceLogs(invoice.id).then(r => setSelectedInvoice(prev => ({ ...prev, _logs: r.data || [] }))).catch(() => {});
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedInvoice?.id) return;
    try {
      const res = await crmAPI.addInvoiceNote(selectedInvoice.id, { text: newNote, type: noteType });
      setInvoiceNotes(prev => [res.data, ...prev]);
      setNewNote('');
      toast.success('Note added');
    } catch { toast.error('Failed to add note'); }
  };


  // Calculate collection rate
  const collectionRate = stats?.total_invoiced > 0 
    ? (stats.total_paid / stats.total_invoiced) * 100 
    : 0;

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const quarterOptions = [
    { value: 'Q1', label: 'Q1 (Jan-Mar)' },
    { value: 'Q2', label: 'Q2 (Apr-Jun)' },
    { value: 'Q3', label: 'Q3 (Jul-Sep)' },
    { value: 'Q4', label: 'Q4 (Oct-Dec)' }
  ];

  return (
    <div className="space-y-6 p-6" data-testid="invoices-page">
      {/* Header */}
      {/* Account filter banner — shown when navigating from Accounts page */}
      {fromAccountName && (
        <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-xs">
          <span className="text-blue-700 flex items-center gap-1.5">
            <span className="font-semibold">Filtered by account:</span> {decodeURIComponent(fromAccountName)}
          </span>
          <button
            onClick={() => { setFilters(f => ({...f, account: null})); setSearchParams({}); }}
            className="text-blue-500 hover:text-blue-700 font-medium"
          >
            Clear filter ×
          </button>
        </div>
      )}

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Invoices & Receivables</h1>
          <p className="text-muted-foreground">Track payments and manage invoices</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={displayMode} onValueChange={setDisplayMode}>
            <SelectTrigger className="w-32 h-9" data-testid="invoice-display-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="compact">Compact</SelectItem>
              <SelectItem value="exact">Exact</SelectItem>
            </SelectContent>
          </Select>
          <CompactBusinessRules
            scopeItems={[filters.year, filters.quarter, filters.account, filters.salesperson].filter(Boolean)}
            ruleKeys={['invoice_progress', 'collection_status', 'totals_scope']}
            label="Rules"
            dataTestId="invoices-compact-business-rules"
          />
          <Select value={selectedCurrency} onValueChange={handleCurrencyChange}>
            <SelectTrigger className="w-[120px]" data-testid="currency-selector">
              <SelectValue placeholder="Currency" />
            </SelectTrigger>
            <SelectContent>
              {getCurrencyOptions().map((curr) => (
                <SelectItem key={curr.code} value={curr.code}>
                  {curr.code === 'OMR' || curr.code === 'SAR' ? curr.code : `${curr.symbol} ${curr.code}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={loadInvoices} variant="outline" data-testid="refresh-invoices">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button 
            variant="outline"
            onClick={async () => {
              try {
                const { toast } = await import('sonner');
                toast.loading('Syncing invoice data from Odoo...');
                const res = await crmAPI.resyncInvoices();
                toast.dismiss();
                const r = res.data;
                if (r.status === 'already_correct') {
                  toast.success('Invoice data is already in sync with Odoo');
                } else {
                  toast.success(`Sync complete: ${r.summary}`);
                }
                loadInvoices();
              } catch (e) {
                const { toast } = await import('sonner');
                toast.error('Sync failed: ' + (e.response?.data?.detail || e.message));
              }
            }}
            data-testid="resync-invoices-btn"
            title="Sync invoice data from Odoo to fix any discrepancies"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Sync from Odoo
          </Button>
          <Button className="bg-primary hover:bg-primary/90" data-testid="create-invoice-btn">
            <Plus className="h-4 w-4 mr-2" />
            Create Invoice
          </Button>
        </div>
      </div>

      {/* Contextual Filters Bar */}
      <div className="flex flex-wrap items-center gap-3 p-4 bg-gradient-to-r from-muted/30 to-muted/50 rounded-lg border" data-testid="invoice-filters">
        <div className="flex items-center gap-2 mr-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-muted-foreground">Filters</span>
        </div>

        {/* Year Filter */}
        <Select 
          value={filters.year || 'all'} 
          onValueChange={(v) => updateFilter('year', v === 'all' ? null : v)}
        >
          <SelectTrigger className="w-[110px] h-9" data-testid="filter-year">
            <Calendar className="h-3 w-3 mr-1" />
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Years</SelectItem>
            {filterOptions.years?.map(year => (
              <SelectItem key={year} value={year}>{year}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Quarter Filter */}
        <Select 
          value={filters.quarter || 'all'} 
          onValueChange={(v) => updateFilter('quarter', v === 'all' ? null : v)}
        >
          <SelectTrigger className="w-[140px] h-9" data-testid="filter-quarter">
            <SelectValue placeholder="Quarter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Quarters</SelectItem>
            {quarterOptions.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Account Filter - Searchable */}
        <Popover open={accountOpen} onOpenChange={setAccountOpen}>
          <PopoverTrigger asChild>
            <Button 
              variant="outline" 
              role="combobox" 
              className="w-[180px] h-9 justify-between"
              data-testid="filter-account"
            >
              <Building2 className="h-3 w-3 mr-1 shrink-0" />
              <span className="truncate">
                {filters.account || "All Accounts"}
              </span>
              <ChevronDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0">
            <Command shouldFilter={true}>
              <CommandInput placeholder="Search account..." />
              <CommandList>
                <CommandEmpty>No account found.</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    value="all-accounts"
                    onSelect={() => {
                      updateFilter('account', null);
                      setAccountOpen(false);
                    }}
                  >
                    All Accounts
                  </CommandItem>
                  {filterOptions.accounts?.map((acc) => (
                    <CommandItem
                      key={acc}
                      value={acc}
                      onSelect={(value) => {
                        updateFilter('account', value);
                        setAccountOpen(false);
                      }}
                    >
                      {acc}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Salesperson Filter */}
        <Select value={filters.salesperson || 'all'} onValueChange={v => updateFilter('salesperson', v === 'all' ? null : v)}>
          <SelectTrigger className="w-[160px] h-9"><SelectValue placeholder="All Sales Reps" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sales Reps</SelectItem>
            {filterOptions.salespersons?.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>

        {/* Product Manager Filter */}
        <Select value={filters.product_manager || 'all'} onValueChange={v => updateFilter('product_manager', v === 'all' ? null : v)}>
          <SelectTrigger className="w-[170px] h-9"><SelectValue placeholder="All Product Mgrs" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Product Mgrs</SelectItem>
            {filterOptions.product_managers?.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>

        {/* Solution Category Filter */}
        <Select value={filters.solution_category || 'all'} onValueChange={v => updateFilter('solution_category', v === 'all' ? null : v)}>
          <SelectTrigger className="w-[170px] h-9"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {filterOptions.solution_categories?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>

        {/* Reset Button */}
        {hasActiveFilters() && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={resetFilters}
            className="h-9 text-muted-foreground hover:text-foreground"
            data-testid="filter-reset"
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            Reset
          </Button>
        )}
        
        {/* Export Excel */}
        <Button variant="outline" size="sm" className="h-9 ml-auto"
          onClick={() => exportInvoicesRows(filteredInvoices, 'invoices_export.xlsx')} data-testid="export-excel-btn">
          <Download className="h-4 w-4 mr-1" /> Export Excel
        </Button>

        {/* Active Filter Summary */}
        {hasActiveFilters() && (
          <div className="ml-auto">
            <Badge variant="secondary" className="text-xs">
              {[filters.year, filters.quarter, filters.account, filters.salesperson, filters.product_manager, filters.solution_category].filter(Boolean).join(' • ')}
            </Badge>
          </div>
        )}
      </div>

      <DataFreshnessBar entityIds={['invoices', 'invoice_lines', 'invoice_log_messages']} actionEntityIds={['invoices']} title="Invoice Data Freshness" dataTestId="invoice-data-freshness" />

      {/* Stats Cards — Clickable Drill-Down */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Invoiced', value: stats?.total_invoiced, count: stats?.count_total, icon: FileText, color: 'text-foreground', bgIcon: 'bg-primary/10', iconColor: 'text-primary', status: null },
          { label: 'Due Soon', value: stats?.total_pending, count: stats?.count_pending, icon: Clock, color: 'text-amber-600', bgIcon: 'bg-amber-100', iconColor: 'text-amber-600', status: 'pending',
            tooltip: 'Due Soon = invoices not yet paid, but due date is still in the future.' },
          { label: 'Overdue', value: stats?.total_overdue, count: stats?.count_overdue, icon: AlertTriangle, color: 'text-red-600', bgIcon: 'bg-red-100', iconColor: 'text-red-600', status: 'overdue' },
          { label: 'Collected', value: stats?.total_paid, count: stats?.count_paid, icon: CheckCircle, color: 'text-emerald-600', bgIcon: 'bg-emerald-100', iconColor: 'text-emerald-600', status: 'paid' },
        ].map(kpi => { const KI = kpi.icon; return (
          <Card key={kpi.label} className={`cursor-pointer transition-all hover:shadow-md ${statusDrill === kpi.status && kpi.status ? 'ring-2 ring-[#800000] ring-offset-1' : ''}`}
            onClick={() => { setStatusDrill(statusDrill === kpi.status ? null : kpi.status); setAgingFilter(null); }}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm text-muted-foreground">{kpi.label}</p>
                    {statusDrill === kpi.status && kpi.status && <Badge className="bg-[#800000] text-white text-[9px]">Filtered</Badge>}
                    {kpi.tooltip && (
                      <span title={kpi.tooltip} className="cursor-help text-gray-400 hover:text-gray-600 text-[10px] border border-gray-300 rounded-full w-3.5 h-3.5 flex items-center justify-center font-bold">?</span>
                    )}
                  </div>
                  <p className={`text-[clamp(1.85rem,2.2vw,2.9rem)] font-bold ${kpi.color}`}>{displayMoney(kpi.value || 0)}</p>
                  <p className="text-xs text-muted-foreground">{kpi.count || 0} invoices</p>
                  {kpi.label === 'Pending' && (kpi.count || 0) === 0 && (
                    <p className="text-[10px] text-amber-500 mt-0.5">All outstanding invoices are past due (overdue)</p>
                  )}
                </div>
                <div className={`h-12 w-12 rounded-full ${kpi.bgIcon} flex items-center justify-center`}><KI className={`h-6 w-6 ${kpi.iconColor}`} /></div>
              </div>
            </CardContent>
          </Card>); })}
      </div>

      {(statusDrill || agingFilter) && (
        <div className="flex items-center gap-2 bg-[#800000]/5 border border-[#800000]/20 rounded-lg px-4 py-2">
          <span className="text-sm text-[#800000] font-medium">Drill-down: {statusDrill ? statusDrill + ' invoices' : ''} {agingFilter ? 'aging ' + agingFilter.replace('_','-') + ' days' : ''}</span>
          <Button variant="ghost" size="sm" onClick={() => { setStatusDrill(null); setAgingFilter(null); }} className="h-6 text-xs text-[#800000]">Clear filter</Button>
          <span className="text-xs text-gray-500 ml-auto">{filteredInvoices.length} results</span>
        </div>
      )}

      {/* Collection Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Collection Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Collection Rate</span>
              <span className="font-medium">
                {displayMoney(stats?.total_paid || 0)} / {displayMoney(stats?.total_invoiced || 0)} 
                ({collectionRate.toFixed(1)}%)
              </span>
            </div>
            <Progress value={collectionRate} className="h-2" />
          </div>
        </CardContent>
      </Card>

      {/* Aging Breakdown */}
      {stats?.aging_breakdown && (stats.aging_breakdown['0_30'] > 0 || stats.aging_breakdown['30_60'] > 0 || stats.aging_breakdown['60_90'] > 0 || stats.aging_breakdown['90_plus'] > 0) && (
        <div className="space-y-3">
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: '0-30 Days', key: '0_30', color: 'text-amber-600 bg-amber-50', bar: 'bg-amber-400', min: 0, max: 30 },
              { label: '30-60 Days', key: '30_60', color: 'text-orange-600 bg-orange-50', bar: 'bg-orange-500', min: 30, max: 60 },
              { label: '60-90 Days', key: '60_90', color: 'text-red-500 bg-red-50', bar: 'bg-red-500', min: 60, max: 90 },
              { label: '90+ Days', key: '90_plus', color: 'text-red-700 bg-red-100', bar: 'bg-red-700', min: 90, max: 9999 },
            ].map(bucket => (
              <Card key={bucket.key} className={`cursor-pointer transition-all hover:shadow-md ${agingFilter === bucket.key ? 'ring-2 ring-[#800000] ring-offset-1' : ''}`}
                onClick={() => setAgingFilter(agingFilter === bucket.key ? null : bucket.key)}>
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500">{bucket.label}</p>
                    {agingFilter === bucket.key && <Badge className="bg-[#800000] text-white text-[9px]">Filtered</Badge>}
                  </div>
                  <p className={`text-lg font-bold ${bucket.color.split(' ')[0]}`}>{displayMoney(stats.aging_breakdown[bucket.key] || 0)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          {/* Debt Ageing Stacked Bar Chart */}
          <DebtAgeingChart aging={stats.aging_breakdown} formatCurrency={formatCurrency} formatCurrencyCompact={formatCurrencyCompact} displayMode={displayMode} currency={selectedCurrency} onFilter={setAgingFilter} activeFilter={agingFilter} />
        </div>
      )}

      {/* Invoices Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all" data-testid="tab-all">
                  All ({stats?.count_total || 0})
                </TabsTrigger>
                <TabsTrigger value="pending" data-testid="tab-pending">
                  Due Soon ({stats?.count_pending || 0})
                </TabsTrigger>
                <TabsTrigger value="overdue" data-testid="tab-overdue">
                  Overdue ({stats?.count_overdue || 0})
                </TabsTrigger>
                <TabsTrigger value="paid" data-testid="tab-paid">
                  Paid ({stats?.count_paid || 0})
                </TabsTrigger>
                <TabsTrigger value="collections" data-testid="tab-collections" className="flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Collection Queue
                </TabsTrigger>
                <TabsTrigger value="salesperson" data-testid="tab-salesperson" className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  Salesperson Performance
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search invoices..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="invoice-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {activeTab === 'salesperson' ? (
            /* Salesperson Performance Table */
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                <Award className="h-4 w-4" />
                <span>Salesperson invoice + collection performance within the current filter scope</span>
              </div>
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">#</TableHead>
                      <TableHead>Salesperson</TableHead>
                      <TableHead className="text-right">Won Value</TableHead>
                      <TableHead className="text-right">Won Deals</TableHead>
                      <TableHead className="text-right">Billed</TableHead>
                      <TableHead className="text-right">Collected</TableHead>
                      <TableHead className="text-right">Overdue</TableHead>
                      <TableHead className="text-right">Invoices</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salespersonData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          No salesperson data available
                        </TableCell>
                      </TableRow>
                    ) : (
                      salespersonData.map((sp, index) => {
                        const collectionRate = sp.billed > 0 ? (sp.collected / sp.billed) * 100 : 0;
                        return (
                          <TableRow key={sp.salesperson} data-testid={`sp-row-${index}`}>
                            <TableCell>
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium
                                ${index === 0 ? 'bg-amber-100 text-amber-700' : 
                                  index === 1 ? 'bg-gray-200 text-gray-700' : 
                                  index === 2 ? 'bg-orange-100 text-orange-700' : 
                                  'bg-gray-50 text-gray-500'}`}>
                                {index + 1}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center text-white text-xs font-medium">
                                  {sp.salesperson.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                </div>
                                <div className="flex flex-col">
                                  <span className="font-medium truncate max-w-[150px]">{sp.salesperson}</span>
                                  <div className="flex items-center gap-1">
                                    <Progress value={collectionRate} className="w-16 h-1.5" />
                                    <span className="text-xs text-muted-foreground">{collectionRate.toFixed(0)}%</span>
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-mono font-semibold text-emerald-600">
                              {formatCurrency(sp.won_value, selectedCurrency)}
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge variant="outline">{sp.won_count}</Badge>
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(sp.billed, selectedCurrency)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-emerald-600">
                              {formatCurrency(sp.collected, selectedCurrency)}
                            </TableCell>
                            <TableCell className="text-right">
                              {sp.overdue > 0 ? (
                                <span className="font-mono text-red-600 font-medium">
                                  {formatCurrency(sp.overdue, selectedCurrency)}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge variant="secondary">{sp.count_invoices}</Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
              {/* Totals Row */}
              {salespersonData.length > 0 && (
                <div className="border-t pt-4 grid grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Total Won</p>
                    <p className="text-lg font-bold text-emerald-600">
                      {formatCurrency(salespersonData.reduce((sum, sp) => sum + sp.won_value, 0), selectedCurrency)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Total Billed</p>
                    <p className="text-lg font-bold">
                      {formatCurrency(salespersonData.reduce((sum, sp) => sum + sp.billed, 0), selectedCurrency)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Total Collected</p>
                    <p className="text-lg font-bold text-emerald-600">
                      {formatCurrency(salespersonData.reduce((sum, sp) => sum + sp.collected, 0), selectedCurrency)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Total Overdue</p>
                    <p className="text-lg font-bold text-red-600">
                      {formatCurrency(salespersonData.reduce((sum, sp) => sum + sp.overdue, 0), selectedCurrency)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : activeTab === 'collections' ? (
            <CollectionsTab />
          ) : (
            /* Regular Invoice Table */
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>SO #</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Salesperson</TableHead>
                  <TableHead>Product Mgr</TableHead>
                  <TableHead>Invoice Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No invoices found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredInvoices.map((invoice) => (
                    <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          {invoice.invoice_number}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-gray-600">{invoice.so_number || '-'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          {invoice.account}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono font-medium">
                        {formatCurrency(invoice.amount, invoice.currency || selectedCurrency)}
                        {invoice.currency && invoice.currency !== selectedCurrency && (
                          <span className="ml-1 text-[9px] text-muted-foreground font-sans">({invoice.currency})</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{invoice.salesperson || '-'}</TableCell>
                      <TableCell className="text-sm">{invoice.product_manager || '-'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          {formatDate(invoice.invoice_date)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          {formatDate(invoice.due_date)}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button variant="ghost" size="sm" onClick={() => viewInvoice(invoice)} data-testid={`view-invoice-${invoice.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {/* Send Reminder for overdue/pending invoices */}
                          {(invoice.status === 'overdue' || invoice.status === 'pending') && (
                            <Button
                              variant="ghost" size="sm"
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              title="Send payment reminder to customer"
                              data-testid={`send-reminder-${invoice.id}`}
                              onClick={() => {
                                const subject = `Payment Reminder: ${invoice.invoice_number}`;
                                const body = `Dear ${invoice.account},\n\nThis is a friendly reminder that invoice ${invoice.invoice_number} for ${formatCurrency(invoice.amount, invoice.currency || selectedCurrency)} is ${invoice.status === 'overdue' ? 'overdue' : 'due soon for payment'}.\n\nPlease arrange payment at your earliest convenience.\n\nThank you.`;
                                window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
                                toast.success('Email client opened with reminder draft');
                              }}
                            >
                              <Send className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {odooBaseUrl && invoice.source_record_id && (
                            <a href={`${odooBaseUrl}/web#id=${invoice.source_record_id}&model=account.move&view_type=form`}
                              target="_blank" rel="noopener noreferrer"
                              title="Open in Odoo"
                              className="inline-flex items-center justify-center h-8 w-8 text-muted-foreground hover:text-[#800000] transition-colors rounded"
                              data-testid={`invoice-odoo-link-${invoice.id}`}>
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Invoice Detail Sheet — Enhanced */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className={`${sheetExpanded ? 'w-[90vw] sm:w-[85vw] sm:max-w-[1200px]' : 'w-[500px] sm:w-[650px]'} overflow-y-auto transition-all duration-300`}>
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {selectedInvoice?.invoice_number}
              <button onClick={() => setSheetExpanded(!sheetExpanded)} className="ml-auto p-1.5 rounded-lg hover:bg-gray-100 transition-colors" title="Double-click or click to expand/collapse">
                {sheetExpanded ? <Minimize2 className="h-4 w-4 text-gray-500" /> : <Maximize2 className="h-4 w-4 text-gray-500" />}
              </button>
            </SheetTitle>
          </SheetHeader>
          
          {selectedInvoice && (
            <div className="mt-4 space-y-5">
              {/* Status + Amount Header */}
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs text-muted-foreground">Bill To</p>
                  <p className="font-semibold text-lg">{selectedInvoice.account}</p>
                </div>
                {getStatusBadge(selectedInvoice.status)}
              </div>

              <div className="flex justify-between items-center p-4 bg-muted rounded-lg">
                <span className="font-medium">Total Amount</span>
                <span className="text-2xl font-bold">{formatCurrency(selectedInvoice.amount, selectedInvoice.currency || selectedCurrency)}</span>
              </div>

              {/* Key Dates */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 border">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Invoice Date</p>
                  <p className="text-sm font-medium mt-0.5">{formatDate(selectedInvoice.invoice_date) || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 border">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Due Date</p>
                  <p className="text-sm font-medium mt-0.5">{formatDate(selectedInvoice.due_date) || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 border">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Aging</p>
                  <p className={`text-sm font-bold mt-0.5 ${selectedInvoice.aging_days > 60 ? 'text-red-600' : selectedInvoice.aging_days > 30 ? 'text-amber-600' : 'text-green-600'}`}>
                    {selectedInvoice.status === 'overdue' ? `${selectedInvoice.aging_days || 0} days overdue` : selectedInvoice.status === 'paid' ? 'Paid' : 'Current'}
                  </p>
                </div>
              </div>

              {/* Sales & Product Info */}
              <Card>
                <CardContent className="pt-4 space-y-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Sales Information</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-gray-400">SO Number</p>
                      <p className="font-medium">{selectedInvoice.so_number || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Salesperson</p>
                      <p className="font-medium">{selectedInvoice.salesperson || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Product Manager</p>
                      <p className="font-medium">{selectedInvoice.product_manager ? <span className="text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded text-xs">{selectedInvoice.product_manager}</span> : '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Solution Category</p>
                      <p className="font-medium">{selectedInvoice.solution_category ? <span className="text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded text-xs">{selectedInvoice.solution_category}</span> : '-'}</p>
                    </div>
                  </div>
                  {selectedInvoice.opportunity_name && (
                    <div>
                      <p className="text-xs text-gray-400">Linked Opportunity</p>
                      <p className="text-sm font-medium text-[#800000]">{selectedInvoice.opportunity_name}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Invoice Line Items */}
              <Card>
                <CardContent className="pt-4 space-y-3">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                    Invoice Lines
                    {selectedInvoice._lines?.length > 0 && <Badge variant="secondary" className="text-[10px]">{selectedInvoice._lines.length} items</Badge>}
                  </div>
                  {!selectedInvoice._lines ? (
                    <p className="text-xs text-gray-400 text-center py-2">Loading...</p>
                  ) : selectedInvoice._lines.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-2">No line items found</p>
                  ) : (
                    <div className="space-y-2">
                      {selectedInvoice._lines.map((line, idx) => (
                        <div key={idx} className="flex items-start gap-2 p-2.5 rounded-lg bg-gray-50 border border-gray-100">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-800 truncate">{line.description || line.product_name || '-'}</p>
                            {line.product_name && line.product_name !== line.description && (
                              <p className="text-[10px] text-gray-400 truncate">{line.product_name}</p>
                            )}
                            <p className="text-[10px] text-gray-400">{line.quantity} × {line.uom || 'Units'} @ {formatCurrency(line.price_unit, selectedInvoice?.currency || selectedCurrency)}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-sm font-bold text-gray-900">{formatCurrency(line.price_subtotal, selectedInvoice?.currency || selectedCurrency)}</p>
                          </div>
                        </div>
                      ))}
                      {/* Totals */}
                      <div className="border-t pt-2 space-y-1">
                        {selectedInvoice._lineTotal?.subtotal > 0 && (
                          <div className="flex justify-between text-xs text-gray-500 py-0.5">
                            <span>Subtotal (excl. tax)</span>
                            <span className="font-mono">{formatCurrency(selectedInvoice._lineTotal.subtotal, selectedCurrency)}</span>
                          </div>
                        )}
                        {selectedInvoice._lineTotal?.tax_total > 0 && (
                          <div className="flex justify-between text-xs text-gray-500 py-0.5">
                            <span>Tax (VAT)</span>
                            <span className="font-mono">{formatCurrency(selectedInvoice._lineTotal.tax_total, selectedCurrency)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm font-bold py-0.5 border-t">
                          <span>Total</span>
                          <span className="font-mono">{formatCurrency(selectedInvoice.amount, selectedCurrency)}</span>
                        </div>
                        <div className="flex justify-between text-sm py-0.5">
                          <span className="text-gray-500">Amount Due</span>
                          <span className={`font-mono font-bold ${selectedInvoice.amount_residual > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {formatCurrency(selectedInvoice.amount_residual || 0, selectedCurrency)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Payment & Collection */}
              <Card>
                <CardContent className="pt-4 space-y-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Payment Status</p>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex justify-between text-xs mb-1">
                        <span>Collection Progress</span>
                        <span>{selectedInvoice.amount > 0 ? Math.round(((selectedInvoice.amount - (selectedInvoice.amount_residual || 0)) / selectedInvoice.amount) * 100) : 0}%</span>
                      </div>
                      <Progress value={selectedInvoice.amount > 0 ? ((selectedInvoice.amount - (selectedInvoice.amount_residual || 0)) / selectedInvoice.amount) * 100 : 0} className="h-2" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm mt-2">
                    <div className="bg-green-50 rounded-lg p-2 border border-green-100">
                      <p className="text-[10px] text-green-600">Collected</p>
                      <p className="font-bold text-green-700">{formatCurrency(selectedInvoice.amount - (selectedInvoice.amount_residual || 0), selectedCurrency)}</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-2 border border-red-100">
                      <p className="text-[10px] text-red-600">Outstanding</p>
                      <p className="font-bold text-red-700">{formatCurrency(selectedInvoice.amount_residual || 0, selectedCurrency)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Activity Log from Odoo */}
              {selectedInvoice._logs?.length > 0 && (
                <Card>
                  <CardContent className="pt-4 space-y-3">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                      Activity Log
                      <Badge variant="secondary" className="text-[10px]">{selectedInvoice._logs.length}</Badge>
                    </div>
                    <div className="space-y-2 max-h-[250px] overflow-y-auto">
                      {selectedInvoice._logs.map((log, idx) => {
                        const isStatus = log.is_status_change || log.subtype;
                        const border = isStatus ? 'border-l-amber-400 bg-amber-50' : 'border-l-blue-300 bg-blue-50';
                        return (
                          <div key={idx} className={`flex gap-2.5 p-2.5 rounded-lg border-l-4 ${border}`}>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-gray-800 break-words">{log.body}</p>
                              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                <span className="text-[10px] text-gray-500">{log.author}</span>
                                {log.subtype && <Badge variant="outline" className="text-[9px] border-amber-300 text-amber-700 py-0">{log.subtype}</Badge>}
                              </div>
                            </div>
                            <span className="text-[10px] text-gray-400 shrink-0">{log.date ? new Date(log.date).toLocaleDateString() : ''}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Collection Notes & Follow-up Logs */}
              <Card>
                <CardContent className="pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Collection Notes</p>
                    <Badge variant="outline" className="text-[10px]">{invoiceNotes.length} notes</Badge>
                  </div>
                  {/* Add Note Form */}
                  <div className="flex gap-2">
                    <Input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a note..." className="flex-1 h-8 text-sm" onKeyDown={e => { if (e.key === 'Enter' && newNote.trim()) handleAddNote(); }} />
                    <Select value={noteType} onValueChange={setNoteType}>
                      <SelectTrigger className="w-[100px] h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="note">Note</SelectItem>
                        <SelectItem value="followup">Follow-up</SelectItem>
                        <SelectItem value="reminder">Reminder</SelectItem>
                        <SelectItem value="payment">Payment</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="sm" className="h-8 bg-[#800000] hover:bg-[#9a1919] text-white" disabled={!newNote.trim()} onClick={handleAddNote}>Add</Button>
                  </div>
                  {/* Notes List */}
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {invoiceNotes.length === 0 ? (
                      <p className="text-xs text-gray-400 text-center py-3">No collection notes yet</p>
                    ) : invoiceNotes.map(note => (
                      <div key={note.id} className="bg-gray-50 rounded-lg p-2.5 border border-gray-100">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={`text-[9px] ${note.type === 'payment' ? 'bg-green-100 text-green-700' : note.type === 'followup' ? 'bg-blue-100 text-blue-700' : note.type === 'reminder' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{note.type}</Badge>
                          <span className="text-[10px] text-gray-500">{note.author}</span>
                          <span className="text-[10px] text-gray-400 ml-auto">{new Date(note.created_at).toLocaleDateString()} {new Date(note.created_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</span>
                        </div>
                        <p className="text-xs text-gray-700">{note.text}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1">
                  <Download className="h-4 w-4 mr-2" /> Download PDF
                </Button>
                {selectedInvoice.status !== 'paid' && (
                  <Button className="flex-1 bg-[#800000] hover:bg-[#9a1919] text-white">
                    <Send className="h-4 w-4 mr-2" /> Send Reminder
                  </Button>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function DebtAgeingChart({ aging, formatCurrency, formatCurrencyCompact, displayMode, currency, onFilter, activeFilter }) {
  const buckets = [
    { key: '0_30', label: '0–30d', bar: 'bg-amber-400', tip: '0-30 Days' },
    { key: '30_60', label: '31–60d', bar: 'bg-orange-500', tip: '31-60 Days' },
    { key: '60_90', label: '61–90d', bar: 'bg-red-500', tip: '61-90 Days' },
    { key: '90_plus', label: '90+d', bar: 'bg-red-700', tip: 'Over 90 Days' },
  ];
  const total = buckets.reduce((s, b) => s + (aging[b.key] || 0), 0);
  const money = (value) => displayMode === 'compact' ? formatCurrencyCompact(value) : formatCurrency(value || 0, currency);
  if (total === 0) return null;
  return (
    <div className="rounded-lg border p-3 bg-muted/20" data-testid="debt-ageing-chart">
      <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
        <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
        Debt Ageing — Total Overdue: <span className="font-bold text-foreground">{money(total)}</span>
      </p>
      <div className="flex h-9 rounded-lg overflow-hidden gap-0.5">
        {buckets.map(b => {
          const pct = (aging[b.key] || 0) / total * 100;
          if (pct < 0.5) return null;
          return (
            <div key={b.key}
              className={`${b.bar} ${activeFilter === b.key ? 'ring-2 ring-inset ring-white' : ''} flex items-center justify-center text-white text-[10px] font-bold cursor-pointer hover:opacity-85 transition-all`}
              style={{ width: `${pct}%` }}
              onClick={() => onFilter(activeFilter === b.key ? null : b.key)}
              title={`${b.tip}: ${formatCurrency(aging[b.key] || 0, currency)} (${pct.toFixed(0)}%)`}
            >
              {pct > 12 ? `${pct.toFixed(0)}%` : ''}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
        {buckets.map(b => {
          const amt = aging[b.key] || 0;
          if (!amt) return null;
          return (
            <button key={b.key} onClick={() => onFilter(activeFilter === b.key ? null : b.key)}
              className={`flex items-center gap-1 text-[10px] transition-colors ${activeFilter === b.key ? 'font-bold text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
              <span className={`w-2.5 h-2.5 rounded-sm ${b.bar} inline-block shrink-0`} />
              {b.label}: {money(amt)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

